/**
 * A business's day, worked out from the records the couriers' phones write: one engine for the server's daily totals
 * (daily_totals.ts) and the web control room (control-room/, imported as `@day`). Pure TypeScript, no Firebase: the
 * callers read the documents and hand them in as plain data, timestamps and all.
 *
 * It follows the owner's phone so the two can't disagree: `LiveToday.compute` (lib/services/business/live_today.dart),
 * the running route's sums (`ActiveRouteSession.arrivalIn`, lib/providers/active_route_provider.dart), the money
 * (`MoneyTotals`, lib/services/payments/money.dart; `CourierBalance`, settling.dart) and the stats
 * (`CourierStats`, lib/services/stats/courier_stats.dart). tests/live_day.test.ts in control-room/ runs the phone's
 * cases (test/live_test.dart) against it.
 *
 * Days are the business's own (docs/where-users-are.md, "A business's day"): from `businesses/{id}.where.timeZone`.
 */

// ── Time ────────────────────────────────────────────────────────────────────

export const MINUTE = 60_000;
export const HOUR = 60 * MINUTE;
export const DAY = 24 * HOUR;

/** The zone a business's day runs in when it hasn't said: the app launches in Nigeria. */
export const DEFAULT_ZONE = 'Africa/Lagos';

const partsCache = new Map<string, Intl.DateTimeFormat>();
function zoneFormat(zone: string): Intl.DateTimeFormat {
  let f = partsCache.get(zone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      weekday: 'short',
    });
    partsCache.set(zone, f);
  }
  return f;
}

/** A moment's wall clock in [zone]. */
export interface Wall {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 Sunday … 6 Saturday. */
  weekday: number;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function wall(t: number, zone: string): Wall {
  const p: Record<string, string> = {};
  for (const part of zoneFormat(zone).formatToParts(new Date(t))) p[part.type] = part.value;
  return {
    year: Number(p.year),
    month: Number(p.month),
    day: Number(p.day),
    hour: Number(p.hour) % 24,
    minute: Number(p.minute),
    second: Number(p.second),
    weekday: WEEKDAYS.indexOf(p.weekday),
  };
}

/** How far [zone]'s clock is ahead of UTC at [t]. */
export function zoneOffset(t: number, zone: string): number {
  const w = wall(t, zone);
  return Date.UTC(w.year, w.month - 1, w.day, w.hour, w.minute, w.second) - Math.floor(t / 1000) * 1000;
}

/** The moment [zone]'s clock shows this wall time. */
export function fromWall(year: number, month: number, day: number, hour = 0, minute = 0, second = 0, zone: string): number {
  const guess = Date.UTC(year, month - 1, day, hour, minute, second);
  const first = guess - zoneOffset(guess, zone);
  // Across a clock change the offset at the answer can differ from the guess's.
  return guess - zoneOffset(first, zone);
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** "2026-10-30": the business's day [t] falls on. */
export function dayOf(t: number, zone: string): string {
  const w = wall(t, zone);
  return `${w.year}-${pad(w.month)}-${pad(w.day)}`;
}

/** Midnight at the start of [day] ("2026-10-30") in [zone]. */
export function dayStart(day: string, zone: string): number {
  const [y, m, d] = day.split('-').map(Number);
  return fromWall(y, m, d, 0, 0, 0, zone);
}

/** [day] moved by [n] days. */
export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** 0 Sunday … 6 Saturday, for a day. */
export function weekdayOf(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Whether [zone] is a time zone this runtime knows. */
export function knownZone(zone: unknown): zone is string {
  if (typeof zone !== 'string' || !zone) return false;
  try {
    zoneFormat(zone);
    return true;
  } catch {
    return false;
  }
}

/** The zone a business's day runs in: `where.timeZone` when the owner confirmed one. */
export function zoneOf(business: unknown, fallback = DEFAULT_ZONE): string {
  const where = isRecord(business) && isRecord(business.where) ? business.where : null;
  return where && knownZone(where.timeZone) ? where.timeZone : fallback;
}

// ── Reading Firestore values ───────────────────────────────────────────────

export type Doc = Record<string, unknown>;

export const isRecord = (v: unknown): v is Doc => typeof v === 'object' && v !== null && !Array.isArray(v);

const ISO_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;
const ISO_LOCAL = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;

/**
 * A moment from a Firestore value: a Timestamp (either SDK's), a Date, milliseconds, or a string. Dart writes a phone's
 * local time with no zone ("2026-10-30T13:00:00.000": `deliverBy` on a sent route's stops), read as the business's
 * wall clock in [zone].
 */
export function ms(v: unknown, zone: string = DEFAULT_ZONE): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v.getTime() : null;
  if (typeof v === 'string') {
    const s = v.trim();
    if (ISO_OFFSET.test(s)) {
      const t = Date.parse(s);
      return Number.isFinite(t) ? t : null;
    }
    const m = ISO_LOCAL.exec(s);
    if (!m) return null;
    return fromWall(+m[1], +m[2], +m[3], +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0), zone);
  }
  if (isRecord(v)) {
    if (typeof v.toMillis === 'function') return (v.toMillis as () => number)();
    const seconds = v.seconds ?? v._seconds;
    const nanos = v.nanoseconds ?? v._nanoseconds ?? 0;
    if (typeof seconds === 'number') return seconds * 1000 + Math.floor(Number(nanos) / 1e6);
  }
  return null;
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const int = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? Math.trunc(v) : undefined);
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const strings = (v: unknown): string[] => list(v).filter((x): x is string => typeof x === 'string');
const record = (v: unknown): Doc => (isRecord(v) ? v : {});

// ── What a stop is ─────────────────────────────────────────────────────────

/** Why a stop wasn't delivered (`FailureReason` in delivery_stop.dart), with the courier's words. */
export const REASONS = {
  noOneThere: 'No one there',
  noAccess: 'Couldn’t get in',
  wrongAddress: 'Wrong address',
  refused: 'Refused',
  closed: 'Closed',
  anotherDay: 'Another day',
  couldntPay: 'Couldn’t pay',
  other: 'Other',
} as const;
export type Reason = keyof typeof REASONS;
const isReason = (v: unknown): v is Reason => typeof v === 'string' && v in REASONS;

/** Nobody's fault, or the customer's own choice: left out of the first-time rate. */
export const excused = (r: Reason | null | undefined) => r === 'closed' || r === 'anotherDay';

export type PaidHow = 'cash' | 'transfer' | 'pos' | 'unpaid';
const isPaidHow = (v: unknown): v is PaidHow => v === 'cash' || v === 'transfer' || v === 'pos' || v === 'unpaid';

/**
 * Where a stop stands. `left`: on a route that ended before it was reached (not attempted). `waiting`: on a route sent
 * and not started yet.
 */
export type StopState = 'done' | 'failed' | 'current' | 'ahead' | 'waiting' | 'left';

export interface Spot {
  lat: number;
  lng: number;
}

export interface Stop {
  id: string;
  /** 1-based, in the route's order. */
  n: number;
  routeId: string;
  riderId: string;
  /** The address as kept (Google's wording, often); [street] and [area] are cut from it. */
  address: string;
  street: string;
  area: string;
  houseNumber?: string;
  placeId?: string;
  /** The address as the business or customer wrote it: what may leave Routr (route_export.dart). */
  writtenAddress?: string;
  /** Google's position; null once it's gone (30 days) and the courier's own isn't there either. */
  spot: Spot | null;
  /** The position is only roughly the address (a plus code, an area, or where the courier marked it). */
  rough: boolean;
  type: 'delivery' | 'pickup';
  state: StopState;
  /** When it was delivered or not. */
  at: number | null;
  /** When the courier should get there, while the route runs. */
  eta: number | null;
  deliverBy: number | null;
  reason: Reason | null;
  /** "Another day": the day the customer asked for ("2026-11-02"). */
  movedTo: string | null;
  name?: string;
  phone?: string;
  notes?: string;
  access?: string;
  courierNote?: string;
  packages: number;
  collectOrder: number;
  collectFee: number;
  currency?: string;
  paidHow?: PaidHow;
  paidAmount?: number;
  paidToCourier?: boolean;
  photos: string[];
  /** Earlier visits that came back later the same day: a stop tried again isn't delivered first time. */
  attempts: number;
  /** Each earlier visit: when, and why it wasn't done then (`resume.attempts` / the record's `attempts`). */
  tries: { at: number | null; reason: Reason | null }[];
  /** Where the courier's phone was when they marked it (ours to keep). */
  handledAt: Spot | null;
  /** Delivered this far from the address (metres), when that can be said. */
  away: number | null;
  linkSent: boolean;
  trackingCode?: string;
  /** Where two couriers meet to hand a parcel over: no customer, link or photo. */
  handover: boolean;
  sentStopId?: string;
  retryOf?: string;
  pickupId?: string;
  dwellMinutes?: number;
  breakMinutes?: number;
}

/** What's to be collected at the door (minor units). */
export const toCollect = (s: Pick<Stop, 'collectOrder' | 'collectFee'>) => s.collectOrder + s.collectFee;
export const handled = (s: Pick<Stop, 'state'>) => s.state === 'done' || s.state === 'failed';
const paid = (s: Stop) => s.paidHow != null && s.paidHow !== 'unpaid';

/** The first part of an address ("12 Danube Street"), and what follows it without a country or postcode ("Maitama"). */
export function splitAddress(address: string): { street: string; area: string } {
  const parts = address
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  const street = parts[0] ?? address;
  const rest = parts
    .slice(1)
    .map((p) => p.replace(/\b\d{5,6}\b/g, '').trim())
    .filter((p) => p && !/^(nigeria|ghana|kenya|united kingdom|united states|federal capital territory)$/i.test(p));
  return { street, area: rest[0] ?? '' };
}

/** Straight-line metres between two spots (HaversineOptimizer.distanceMeters). */
export function metres(a: Spot, b: Spot): number {
  const R = 6371000;
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat);
  const dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

/** A delivery marked further than this from its address (live_today.dart `farAfter`). */
export const FAR_AFTER_M = 300;

const spotOf = (lat: unknown, lng: unknown): Spot | null => {
  const a = num(lat);
  const b = num(lng);
  return a != null && b != null ? { lat: a, lng: b } : null;
};

const spotFrom = (v: unknown): Spot | null => {
  if (Array.isArray(v) && v.length === 2) return spotOf(v[0], v[1]);
  if (isRecord(v)) return spotOf(v.lat, v.lng);
  return null;
};

// ── What a route is ────────────────────────────────────────────────────────

/**
 * `running`: started and not ended. `waiting`: sent by the business, not started. `declined`: the courier can't take
 * it. `done`: every stop handled. `ended`: ended early (some stops not attempted).
 */
export type RouteState = 'running' | 'waiting' | 'declined' | 'done' | 'ended';

export interface Route {
  id: string;
  businessId: string;
  riderId: string;
  name: string | null;
  state: RouteState;
  startedAt: number | null;
  completedAt: number | null;
  endedAt: number | null;
  /** 'cleanup': a server job ended it, nobody chose to. */
  endedBy: string | null;
  sentAt: number | null;
  seenAt: number | null;
  orderChangedAt: number | null;
  declinedAt: number | null;
  declineReason: string | null;
  declineNote: string | null;
  stops: Stop[];
  /** The stop being driven to; [stops].length on the way back or once done. */
  current: number;
  returning: boolean;
  betweenRuns: boolean;
  km: number;
  /** The courier's last position on this route (about every 5 s while it runs). */
  position: (Spot & { at: number | null; bearing: number | null }) | null;
  /** When the courier's phone last sent a position (`lastLocationUpdate`). */
  heardAt: number | null;
  /** The phone stopped sharing for the night until then: no position is no alarm. */
  pausedUntil: number | null;
  /** The optimiser's line (Routes API); kept 29 days. */
  polyline: string | null;
  /** Where the business planned it from (its shop), on a sent route; or where a round trip goes back to. */
  start: (Spot & { label: string }) | null;
  /** The working copy the courier's phone keeps on a running route (`resume`): without it, only the record's counts. */
  working: boolean;
  timePerStopS: number;
  /** Driving into each stop (seconds), in order. */
  legs: number[];
  returnS: number;
  returnStartedAt: number | null;
  breakUntil: number | null;
  touchedAt: number | null;
  lastUndoneAt: number | null;
  riderDeleted: boolean;
  /** The sent route's own record of how it was planned, kept to send it again (`optimization`). */
  optimization: Doc | null;
}

const DEFAULT_TIME_PER_STOP_S = 180;

/** The working copy's stop details (`resume.details`, route_history_service.dart `_details`). */
function details(resume: Doc): Record<string, Doc> {
  const d = record(resume.details);
  return Object.fromEntries(Object.entries(d).filter(([, v]) => isRecord(v))) as Record<string, Doc>;
}

/** Driving into each of [n] stops: the legs when there are as many, else the total split evenly (`drivingLegs`). */
export function drivingLegs(legs: number[] | null, drivingS: number, n: number): number[] {
  if (legs && legs.length === n) return legs;
  if (n === 0) return [];
  return Array.from({ length: n }, () => Math.round(drivingS / n));
}

/**
 * A route's record as the business reads it, with the courier's working copy while it runs. Statuses come from the
 * copy (`resume.done`, `resume.failed`); without one, from the record's own counts (an older app). Null when the
 * record can't be read: left out, not fatal.
 */
export function readRoute(id: string, data: Doc, zone: string = DEFAULT_ZONE): Route | null {
  try {
    const status = str(data.status) ?? 'active';
    const riderId = str(data.riderId);
    const businessId = str(data.businessId);
    if (!riderId || !businessId) return null;
    const sent = status === 'sent' || status === 'declined';
    const resume = record(data.resume);
    const working = status === 'active' && isRecord(data.resume) && isRecord(resume.opt);
    const opt = sent ? record(data.optimization) : record(resume.opt);
    const more = details(resume);
    const done = new Set(strings(resume.done));
    const failed = record(resume.failed);
    const handledTimes = record(resume.handledTimes);
    const handledSpots = record(resume.handledAt);
    const moved = record(resume.movedTo);
    const attempts = record(resume.attempts);
    const links = record(resume.links);
    const shared = new Set([...strings(resume.shared), ...strings(resume.messaged)]);
    const raw = list(data.stops).filter(isRecord);
    const at = (v: unknown) => ms(v, zone);

    const resumeStatus = str(resume.status) ?? 'active';
    const current = working ? Math.min(int(resume.current) ?? 0, raw.length) : Math.min(int(data.currentStopIndex) ?? 0, raw.length);
    const returning = working && resumeStatus === 'returning';
    const betweenRuns = working && resumeStatus === 'reloading';
    const state: RouteState =
      status === 'sent' ? 'waiting' : status === 'declined' ? 'declined' : status === 'completed' ? 'done' : status === 'cancelled' ? 'ended' : 'running';

    // An older app wrote only how far along it was and how many were delivered (`LiveRoute._recordFailed`).
    const anyMarked = raw.some((s) => s.status === 'completed' || s.status === 'failed');
    let countedDone = 0;
    const deliveredBefore = int(data.completedStopCount) ?? 0;
    const stops: Stop[] = raw.map((s, i) => {
      const sid = str(s.id) ?? `stop-${i}`;
      const d = more[sid] ?? {};
      const money = (num(resume.v) ?? 1) >= 2;
      const pick = <T>(key: string, read: (v: unknown) => T | undefined): T | undefined =>
        working && (money || !['collectOrder', 'collectFee', 'currency', 'paidHow', 'paidAmount', 'paidToCourier'].includes(key))
          ? read(d[key])
          : read(s[key]);
      const address = str(s.address) ?? 'An address';
      const google = spotOf(s.lat, s.lng);
      const mine = spotOf(s.doneLat, s.doneLng);
      const handledAtSpot = working ? spotFrom(handledSpots[sid]) : mine;
      // The record's statuses for an ended route; the working copy's while it runs.
      let st: StopState;
      let reason: Reason | null = null;
      if (sent) st = 'waiting';
      else if (working) {
        if (done.has(sid)) st = 'done';
        else if (sid in failed) {
          st = 'failed';
          reason = isReason(failed[sid]) ? failed[sid] : 'other';
        } else st = i === current && !returning && !betweenRuns ? 'current' : 'ahead';
      } else {
        const rs = str(s.status);
        if (rs === 'completed') st = 'done';
        else if (rs === 'failed') {
          st = 'failed';
          reason = isReason(s.failureReason) ? s.failureReason : 'other';
        } else if (state === 'running' && !anyMarked && i < current) {
          st = countedDone++ < deliveredBefore ? 'done' : 'failed';
          if (st === 'failed') reason = 'other';
        } else if (state === 'running') st = i === current ? 'current' : 'ahead';
        else st = 'left';
      }
      const doneAt = working ? at(handledTimes[sid]) : at(s.doneAt);
      const photosRaw = working ? list(record(resume.photos)[sid]).concat(list(record(resume.pods)[sid])) : [...list(s.podPhotoUrls), s.podPhotoUrl];
      const photos = [...new Set(photosRaw.filter((p): p is string => typeof p === 'string' && !!p))];
      const tries = (working ? list(attempts[sid]) : list(s.attempts)).map((x) => {
        const t = record(x);
        return { at: ms(t.at, zone), reason: typeof t.reason === 'string' && t.reason in REASONS ? (t.reason as Reason) : null };
      });
      const movedDay = str(working ? moved[sid] : s.movedTo);
      const { street, area } = splitAddress(address);
      const spot = google ?? mine;
      const stop: Stop = {
        id: sid,
        n: i + 1,
        routeId: id,
        riderId,
        address,
        street,
        area,
        houseNumber: str(s.houseNumber),
        placeId: str(s.placeId),
        writtenAddress: str(s.writtenAddress),
        spot,
        rough: !google || s.precision === 'low',
        type: s.type === 'pickup' ? 'pickup' : 'delivery',
        state: st,
        at: st === 'done' || st === 'failed' ? doneAt : null,
        eta: null,
        deliverBy: at(s.deliverBy),
        reason,
        movedTo: movedDay && /^\d{4}-\d{2}-\d{2}$/.test(movedDay) ? movedDay : null,
        name: sent ? str(s.recipientName) : str(d.recipientName),
        phone: sent ? str(s.recipientPhone) : str(d.recipientPhone),
        notes: sent ? str(s.notes) : str(d.notes),
        access: sent ? str(s.accessInstructions) : str(d.accessInstructions),
        courierNote: sent ? undefined : (str(d.courierNote) ?? str(s.courierNote)),
        packages: int(s.packageCount) ?? 1,
        collectOrder: (sent ? int(s.collectOrder) : pick('collectOrder', int)) ?? 0,
        collectFee: (sent ? int(s.collectFee) : pick('collectFee', int)) ?? 0,
        currency: sent ? str(s.currency) : pick('currency', str),
        paidHow: sent ? undefined : pick('paidHow', (v) => (isPaidHow(v) ? v : undefined)),
        paidAmount: sent ? undefined : pick('paidAmount', int),
        paidToCourier: sent ? undefined : pick('paidToCourier', (v) => (v === true ? true : undefined)),
        photos,
        attempts: tries.length,
        tries,
        handledAt: handledAtSpot,
        away: null,
        linkSent: working ? shared.has(sid) : s.linkSent === true,
        trackingCode: working ? str(links[sid]) : undefined,
        handover: sid.endsWith('-collect') || sid.endsWith('-meet'),
        sentStopId: str(s.sentStopId),
        retryOf: str(s.retryOf),
        pickupId: str(s.pickupId),
        dwellMinutes: sent ? int(s.dwellMinutes) : (int(d.dwellMinutes) ?? int(s.dwellMinutes)),
        breakMinutes: sent ? int(s.breakMinutes) : (int(d.breakMinutes) ?? int(s.breakMinutes)),
      };
      if (st === 'done' && stop.type === 'delivery' && google && stop.handledAt) {
        stop.away = metres(stop.handledAt, google);
      }
      return stop;
    });

    const drivingS = int(sent ? opt.total_duration_s : opt.drivingS) ?? int(data.totalDurationSeconds) ?? 0;
    const legsRaw = list(sent ? opt.leg_s : opt.legS)
      .map((n) => int(n))
      .filter((n): n is number => n != null);
    const position = spotOf(data.currentLat, data.currentLng);
    const startRaw = record(data.start);
    const startSpot = spotOf(startRaw.lat, startRaw.lng);
    const returnTo = spotFrom(sent ? opt.return_to : opt.returnTo);
    return {
      id,
      businessId,
      riderId,
      name: str(data.name) ?? null,
      state,
      startedAt: at(data.startedAt),
      completedAt: at(data.completedAt),
      endedAt: at(data.endedAt),
      endedBy: str(data.endedBy) ?? null,
      sentAt: at(data.sentAt),
      seenAt: at(data.seenAt),
      orderChangedAt: at(data.orderChangedAt),
      declinedAt: at(data.declinedAt),
      declineReason: str(data.declineReason) ?? null,
      declineNote: str(data.declineNote) ?? null,
      stops,
      current,
      returning,
      betweenRuns,
      km: (int(sent ? opt.total_distance_m : data.totalDistanceMeters) ?? 0) / 1000,
      position: position ? { ...position, at: at(data.lastLocationUpdate), bearing: num(data.currentBearing) ?? null } : null,
      heardAt: at(data.lastLocationUpdate),
      pausedUntil: at(data.sharingPausedUntil),
      polyline: str(sent ? opt.polyline : data.polyline) ?? null,
      start: startSpot ? { ...startSpot, label: str(startRaw.label) ?? 'The shop' } : returnTo ? { ...returnTo, label: str(resume.returnLabel) ?? 'The start' } : null,
      working,
      timePerStopS: int(resume.timePerStopS) ?? DEFAULT_TIME_PER_STOP_S,
      legs: drivingLegs(legsRaw.length ? legsRaw : null, drivingS, stops.length),
      returnS: int(sent ? opt.return_s : opt.returnS) ?? 0,
      returnStartedAt: at(resume.returnStartedAt),
      breakUntil: at(resume.breakUntil),
      touchedAt: at(resume.touchedAt),
      lastUndoneAt: at(resume.lastUndoneAt),
      riderDeleted: data.riderDeleted === true,
      optimization: sent && isRecord(data.optimization) ? data.optimization : null,
    };
  } catch {
    return null;
  }
}

// ── A running route's sums (ActiveRouteSession) ────────────────────────────

/** Time at a stop: its own, else the courier's average, plus any break after it (`timeAtStop`). */
export const timeAtStop = (r: Route, s: Stop) => ((s.dwellMinutes != null ? s.dwellMinutes * 60 : r.timePerStopS) + (s.breakMinutes ?? 0) * 60) * 1000;

/** What's left of a break being taken now. */
const breakLeft = (r: Route, now: number) => Math.max(0, (r.breakUntil ?? 0) - now);

/** Time until the courier reaches stop [index]; zero once it's behind. The whole leg into the current stop counts. */
export function arrivalIn(r: Route, index: number, now: number): number {
  if (index < r.current || index >= r.stops.length) return 0;
  let t = breakLeft(r, now);
  for (let i = r.current; i <= index; i++) {
    t += (r.legs[i] ?? 0) * 1000;
    if (i < index) t += timeAtStop(r, r.stops[i]);
  }
  return t;
}

/** Time left from the stop being driven to, the way back included on a round trip. */
export function timeLeft(r: Route, now: number): number {
  if (r.current >= r.stops.length) return 0;
  const last = r.stops.length - 1;
  return arrivalIn(r, last, now) + timeAtStop(r, r.stops[last]) + r.returnS * 1000;
}

/** When the courier will be done: at the last stop, or back at the start on a round trip (`_finish`). */
export function finishAt(r: Route, now: number): number | null {
  if (r.state !== 'running' || !r.working) return null;
  if (r.returning || r.betweenRuns) return r.returning ? Math.max(now, (r.returnStartedAt ?? now) + r.returnS * 1000) : null;
  return now + timeLeft(r, now);
}

/** The times each stop ahead is expected, written onto the stops. */
export function timeStops(r: Route, now: number): void {
  if (r.state !== 'running' || !r.working || r.returning || r.betweenRuns) return;
  for (let i = r.current; i < r.stops.length; i++) {
    const s = r.stops[i];
    if (s.state === 'current' || s.state === 'ahead') s.eta = now + arrivalIn(r, i, now);
  }
}

/** The stop being driven to. */
export const nextStop = (r: Route) => r.stops.find((s) => s.state === 'current') ?? null;

export const delivered = (r: Route) => r.stops.filter((s) => s.state === 'done').length;
export const notDelivered = (r: Route) => r.stops.filter((s) => s.state === 'failed').length;
/** Stops not marked yet. */
export const leftOn = (r: Route) => r.stops.filter((s) => s.state === 'current' || s.state === 'ahead').length;

// ── A route left running (left_running.dart) ───────────────────────────────

/** A route counts as running while it has heard from the courier in this long (business_provider.dart). */
export const RUNNING_WINDOW = 12 * HOUR;
/** The phone shares where the courier is from 5 AM (left_running.dart `shareFrom`). */
export const SHARE_FROM_HOUR = 5;
/** A route nobody touched for a week is out of reach (SessionStore.keepFor). */
export const KEEP_FOR = 7 * DAY;

/** Its last sign of life: its last position, a stop marked or undone, or the courier touching it. */
export function lastSign(r: Route): number {
  const moments = [r.heardAt ?? r.startedAt, r.touchedAt, r.lastUndoneAt, ...(r.working ? [r.startedAt, ...r.stops.map((s) => s.at)] : [])].filter(
    (t): t is number => t != null,
  );
  return moments.length ? Math.max(...moments) : 0;
}

/** Quiet for [after], or past 5 AM with nothing heard today (`LeftRunning.isLeft`). */
export function isLeft(heard: number, now: number, zone: string, after = RUNNING_WINDOW): boolean {
  return now - heard > after || (wall(now, zone).hour >= SHARE_FROM_HOUR && heard < dayStart(dayOf(now, zone), zone));
}

// ── The day ────────────────────────────────────────────────────────────────

export interface Courier {
  uid: string;
  name: string | null;
  phone?: string;
  email?: string;
  vehicle?: string;
  photoUrl?: string;
  joinedAt: number | null;
  status?: string;
}

export function readCourier(uid: string, d: Doc): Courier {
  return {
    uid,
    name: str(d.name) ?? null,
    phone: str(d.phone),
    email: str(d.email),
    vehicle: str(d.vehicleType),
    photoUrl: str(d.photoUrl),
    joinedAt: ms(d.joinedBusinessAt),
    status: str(d.status),
  };
}

/** A courier's rough area while signed in with Routr open (`presence/{uid}`, off duty: ~500 m). */
export interface Presence extends Spot {
  uid: string;
  at: number | null;
  area?: string;
  onDuty: boolean;
}

export function readPresence(uid: string, d: Doc): Presence | null {
  const spot = spotOf(d.lat, d.lng);
  if (!spot) return null;
  return { uid, ...spot, at: ms(d.at), area: str(d.area), onDuty: d.onDuty === true };
}

export type AlertKind = 'late' | 'failedInARow' | 'quiet' | 'farFromAddress' | 'unpaid' | 'anotherDay' | 'stillOpen' | 'notAttempted' | 'notDelivered' | 'leftRoute';
export type Severity = 'needsAction' | 'watch';

const WATCH = new Set<AlertKind>(['quiet', 'unpaid', 'anotherDay', 'notAttempted', 'stillOpen', 'leftRoute']);
export const severityOf = (k: AlertKind): Severity => (WATCH.has(k) ? 'watch' : 'needsAction');

/** One line in Needs a look. Its id stays the same while it's the same problem, so clearing it sticks (LiveAlert). */
export interface Alert {
  id: string;
  kind: AlertKind;
  severity: Severity;
  courierId: string;
  routeId: string;
  stopId?: string;
  /** When the late stop is now expected. */
  expected?: number;
  /** When the quiet courier was last heard from; for one who has left the route, the position that says so. */
  lastHeard?: number;
  /** The stops not delivered in a row, oldest first. */
  failures?: { stopId: string; at: number }[];
  /** How far from its address a stop was marked; how far from the route's line its courier is. */
  metres?: number;
  /** When the stop was marked; for a courier off the route's line, since when. */
  handled?: number;
  /** "Another day": the day asked for. */
  day?: string | null;
  /** Stops not marked, on a route still open or ended early; or a courier's stops not delivered in the last 28 days. */
  left?: number;
  /** A courier's stops not delivered in the last 28 days (`notDeliveredStops`), newest route first. */
  missed?: { routeId: string; stopId: string; at: number | null; notReached: boolean }[];
}

export type EventKind = 'delivered' | 'notDelivered' | 'pickedUp' | 'notPickedUp' | 'started' | 'finished' | 'ended';

export interface DayEvent {
  at: number;
  courierId: string;
  routeId: string;
  kind: EventKind;
  stopId?: string;
  done?: number;
  total?: number;
}

/** Payment at the door, one courier's (`MoneyTotals`). Minor units. */
export interface Money {
  cash: number;
  transfer: number;
  pos: number;
  toCourier: number;
  unpaid: number;
  unpaidStops: number;
  fees: number;
  expected: number;
  forBusiness: number;
  heldForBusiness: number;
  feesWithBusiness: number;
}

export const noMoney = (): Money => ({
  cash: 0,
  transfer: 0,
  pos: 0,
  toCourier: 0,
  unpaid: 0,
  unpaidStops: 0,
  fees: 0,
  expected: 0,
  forBusiness: 0,
  heldForBusiness: 0,
  feesWithBusiness: 0,
});

/** What the courier hands in; below zero, what the business owes them. */
export const handIn = (m: Money) => m.heldForBusiness - m.feesWithBusiness;
export const collected = (m: Money) => m.cash + m.transfer + m.pos + m.toCourier;

/** The business's share and the courier's of what was paid (`sharesOf`): on call keeps the delivery fee. */
export function sharesOf(s: Stop, onCall: boolean): { business: number; courier: number } {
  const p = paid(s) ? (s.paidAmount ?? toCollect(s)) : 0;
  if (!onCall) return { business: p, courier: 0 };
  const business = Math.min(p, s.collectOrder);
  return { business, courier: p - business };
}

/** The money on handled stops (`MoneyTotals.of`). */
export function moneyOf(stops: Iterable<Stop>, onCall: boolean): Money {
  const m = noMoney();
  for (const s of stops) {
    if (s.paidHow == null || toCollect(s) <= 0) continue;
    m.expected += toCollect(s);
    if (s.paidHow === 'unpaid') {
      m.unpaid += toCollect(s);
      m.unpaidStops++;
      continue;
    }
    const amount = s.paidAmount ?? toCollect(s);
    const mine = s.paidHow === 'cash' || s.paidToCourier === true;
    if (s.paidHow === 'cash') m.cash += amount;
    else if (mine) m.toCourier += amount;
    else if (s.paidHow === 'transfer') m.transfer += amount;
    else m.pos += amount;
    m.fees += amount > s.collectOrder ? amount - s.collectOrder : 0;
    const share = sharesOf(s, onCall);
    m.forBusiness += share.business;
    if (mine) m.heldForBusiness += share.business;
    else m.feesWithBusiness += share.courier;
  }
  return m;
}

/** A courier on the business's day. */
export interface CourierDay {
  courier: Courier;
  /** Their latest route running now. */
  running: Route | null;
  /** Their routes that ended today, latest first. */
  finished: Route[];
  /** Sent to them and not started, oldest first. */
  waiting: Route[];
  presence: Presence | null;
  finishAt: number | null;
  /** On a route and nothing heard for 5 minutes. */
  quiet: boolean;
  pausedForNight: boolean;
  leftOpen: Route | null;
  money: Money;
}

export interface Day {
  day: string;
  zone: string;
  now: number;
  delivered: number;
  notDelivered: number;
  /** On routes that have started. */
  toGo: number;
  /** Stops on routes sent and not started. */
  waitingStops: number;
  couriers: CourierDay[];
  /** Today's routes (started today, or still running from before), plus the ones waiting to start. */
  routes: Route[];
  events: DayEvent[];
  /** Needs action before watch, then newest first; cleared ones left out. */
  alerts: Alert[];
  money: Money;
  /** Each running route whose courier is off its line now, and since when (the next run's [DayInput.offRoute]). */
  offRoute: Map<string, number>;
}

/** No position for this long on a route (LiveToday.quietAfter). */
export const QUIET_AFTER = 5 * MINUTE;

// ── Left the route ─────────────────────────────────────────────────────────
// Thresholds the owner may choose later (docs/dispatch-settings.md, Alerts).

/** A courier this far from their route's line has left it: well beyond a phone's fix (50 m off on an open road) or a parallel street. */
export const OFF_ROUTE_M = 500;
/** Off the line this long, by the times of the positions that showed it, Needs a look says so. */
export const LEFT_ROUTE_AFTER = 4 * MINUTE;
/** A stop ahead this far from the line means the line is older than the stops (one added since): nothing is said. */
export const LINE_REACH_M = 400;

/** Google's encoded line (the Routes API's polyline), as points. */
export function decodeLine(encoded: string): Spot[] {
  const out: Spot[] = [];
  let i = 0;
  let lat = 0;
  let lng = 0;
  while (i < encoded.length) {
    for (const k of [0, 1]) {
      let r = 0;
      let sh = 0;
      let b: number;
      do {
        if (i >= encoded.length) return out;
        b = encoded.charCodeAt(i++) - 63;
        r |= (b & 31) << sh;
        sh += 5;
      } while (b >= 32);
      const d = r & 1 ? ~(r >> 1) : r >> 1;
      if (k) lng += d;
      else lat += d;
    }
    out.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return out;
}

/** The shortest distance in metres from [p] to the line through [line]'s points (flat near [p]: well under a metre out over a city). */
export function metresToLine(p: Spot, line: Spot[]): number {
  if (!line.length) return Infinity;
  const kx = 111_320 * Math.cos((p.lat * Math.PI) / 180);
  const ky = 110_540;
  const xy = (s: Spot) => ({ x: (s.lng - p.lng) * kx, y: (s.lat - p.lat) * ky });
  let a = xy(line[0]);
  let best = Math.hypot(a.x, a.y);
  for (let i = 1; i < line.length; i++) {
    const b = xy(line[i]);
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len2 = dx * dx + dy * dy;
    const t = len2 ? Math.max(0, Math.min(1, -(a.x * dx + a.y * dy) / len2)) : 0;
    best = Math.min(best, Math.hypot(a.x + t * dx, a.y + t * dy));
    a = b;
  }
  return best;
}

/**
 * How far a running route's courier is from its line now, when that can be said: on the way to a stop, with a position
 * from the last [QUIET_AFTER], not on a break or stopped for the night, and a line from Google that still reaches every
 * stop ahead (a stop added since, off the old line, would read as leaving it). Null otherwise: nothing is said.
 */
export function awayFromLine(r: Route, now: number): number | null {
  if (r.state !== 'running' || !r.working || r.returning || r.betweenRuns || !r.position || !r.polyline) return null;
  if (r.current >= r.stops.length || (r.breakUntil ?? 0) > now || (r.pausedUntil ?? 0) > now) return null;
  if (r.heardAt == null || now - r.heardAt > QUIET_AFTER) return null;
  const line = decodeLine(r.polyline);
  if (line.length < 2) return null;
  for (let i = r.current; i < r.stops.length; i++) {
    const s = r.stops[i];
    if (s.spot && !handled(s) && metresToLine(s.spot, line) > LINE_REACH_M) return null;
  }
  return metresToLine(r.position, line);
}

/**
 * Where a running route's courier stands against its line: on it (or nothing can be said), off it since [since], or off it
 * for [LEFT_ROUTE_AFTER] or more by the positions' own times ([left]). [was]: since when they were off it at the last look.
 */
export function offRouteOf(r: Route, now: number, was?: number): { away: number; since: number; left: boolean } | null {
  const away = awayFromLine(r, now);
  if (away == null || away <= OFF_ROUTE_M) return null;
  const at = r.heardAt!;
  const since = Math.min(was ?? at, at);
  return { away, since, left: at - since >= LEFT_ROUTE_AFTER };
}

export interface DayInput {
  /** The business's routes: started today, still running, ended today, and sent. More is fine: they're sorted here. */
  routes: Route[];
  couriers: Courier[];
  presence?: Presence[];
  /** Alert ids cleared today, and every route still open that was cleared. */
  cleared?: Set<string>;
  now: number;
  zone: string;
  onCall?: (courierId: string) => boolean;
  /**
   * Since when each running route's courier has been off its line, as the last look found ([Day.offRoute]): a route's
   * record holds only where its courier is now, so whoever works the day out keeps this between looks (the web while
   * it's open, the server in the day's record).
   */
  offRoute?: Map<string, number>;
}

const endOf = (r: Route) => r.completedAt ?? r.endedAt ?? r.startedAt ?? 0;

/**
 * Today for the business (`LiveToday.compute`, with live_provider.dart's choice of routes): the numbers, who's where,
 * what happened and what needs a look. A route counts on the day it started; one still running from before counts
 * today while it isn't left; one started before today and ended today adds only that it ended, and what's left of it.
 */
export function computeDay(input: DayInput): Day {
  const { now, zone } = input;
  const today = dayOf(now, zone);
  const midnight = dayStart(today, zone);
  const cleared = input.cleared ?? new Set<string>();
  const onCall = input.onCall ?? (() => false);
  const couriersById = new Map(input.couriers.map((c) => [c.uid, c]));

  const todays: Route[] = [];
  const leftOpen: Route[] = [];
  const endedEarlier: Route[] = [];
  const waiting: Route[] = [];
  const seen = new Set<string>();
  for (const r of input.routes) {
    if (seen.has(r.id)) continue;
    seen.add(r.id);
    if (r.state === 'waiting' || r.state === 'declined') {
      waiting.push(r);
      continue;
    }
    const started = r.startedAt ?? 0;
    if (r.state === 'running') {
      // A running route left (live_provider.dart `_isLeft`) isn't today's, whenever it started: it's still open.
      const left = isLeft(lastSign(r), now, zone);
      if (!left) todays.push(r);
      // Still open, nobody touched it: Remind (only the business's couriers now, from the last week).
      if (left && !r.riderDeleted && now - lastSign(r) <= KEEP_FOR && couriersById.has(r.riderId)) leftOpen.push(r);
    } else if (started >= midnight) todays.push(r);
    else if (r.endedAt != null && r.endedAt >= midnight && r.endedBy !== 'cleanup') endedEarlier.push(r);
  }

  let deliveredN = 0;
  let notDeliveredN = 0;
  let toGo = 0;
  const events: DayEvent[] = [];
  const alerts: Alert[] = [];
  const running = new Map<string, Route>();
  const finished = new Map<string, Route[]>();
  const handledBy = new Map<string, { stop: Stop; at: number; ok: boolean }[]>();
  const paidStops = new Map<string, Stop[]>();
  const excusedIds = new Set<string>();

  const notAttempted = (r: Route) => {
    const left = r.stops.filter((s) => s.state === 'left').length;
    // Only for a courier who has left: one still in the business has the week's line instead (notDeliveredAlerts).
    if (left === 0 || r.endedBy === 'cleanup' || couriersById.has(r.riderId)) return;
    alerts.push({
      id: `notAttempted:${r.id}`,
      kind: 'notAttempted',
      severity: 'watch',
      courierId: r.riderId,
      routeId: r.id,
      handled: r.endedAt ?? r.completedAt ?? r.heardAt ?? r.startedAt ?? now,
      left,
    });
  };

  for (const r of todays) {
    timeStops(r, now);
    const done = delivered(r);
    const failed = notDelivered(r);
    deliveredN += done;
    notDeliveredN += failed;
    if (r.state === 'running') {
      toGo += Math.max(0, r.stops.length - done - failed);
      const other = running.get(r.riderId);
      if (!other || (r.startedAt ?? 0) > (other.startedAt ?? 0)) running.set(r.riderId, r);
    } else {
      finished.set(r.riderId, [...(finished.get(r.riderId) ?? []), r]);
      notAttempted(r);
    }
    if (r.startedAt != null) events.push({ at: r.startedAt, courierId: r.riderId, routeId: r.id, kind: 'started', total: r.stops.length });
    for (const s of r.stops) {
      if (!handled(s) || s.at == null) continue;
      const ok = s.state === 'done';
      const pickup = s.type === 'pickup';
      events.push({
        at: s.at,
        courierId: r.riderId,
        routeId: r.id,
        stopId: s.id,
        kind: pickup ? (ok ? 'pickedUp' : 'notPickedUp') : ok ? 'delivered' : 'notDelivered',
      });
      handledBy.set(r.riderId, [...(handledBy.get(r.riderId) ?? []), { stop: s, at: s.at, ok }]);
      if (!ok && excused(s.reason)) excusedIds.add(s.id);
      if (!ok && s.reason === 'anotherDay') {
        alerts.push({
          id: `anotherDay:${r.id}:${s.id}${s.movedTo ? `:${s.movedTo}` : ''}`,
          kind: 'anotherDay',
          severity: 'watch',
          courierId: r.riderId,
          routeId: r.id,
          stopId: s.id,
          handled: s.at,
          day: s.movedTo,
        });
      }
      if (toCollect(s) > 0 && s.paidHow != null) {
        paidStops.set(r.riderId, [...(paidStops.get(r.riderId) ?? []), s]);
        if (s.paidHow === 'unpaid') {
          alerts.push({ id: `unpaid:${r.id}:${s.id}`, kind: 'unpaid', severity: 'watch', courierId: r.riderId, routeId: r.id, stopId: s.id, handled: s.at });
        }
      }
      if (ok && !pickup && s.away != null && s.away > FAR_AFTER_M) {
        alerts.push({
          id: `far:${r.id}:${s.id}`,
          kind: 'farFromAddress',
          severity: 'needsAction',
          courierId: r.riderId,
          routeId: r.id,
          stopId: s.id,
          metres: s.away,
          handled: s.at,
        });
      }
    }
    if (r.state === 'done' && r.completedAt != null) {
      events.push({ at: r.completedAt, courierId: r.riderId, routeId: r.id, kind: 'finished', done, total: r.stops.length });
    } else if (r.state === 'ended' && r.endedAt != null && r.endedBy !== 'cleanup') {
      events.push({ at: r.endedAt, courierId: r.riderId, routeId: r.id, kind: 'ended', done, total: r.stops.length });
    }
  }
  for (const r of endedEarlier) {
    finished.set(r.riderId, [...(finished.get(r.riderId) ?? []), r]);
    notAttempted(r);
    if (r.endedAt != null) events.push({ at: r.endedAt, courierId: r.riderId, routeId: r.id, kind: 'ended', done: delivered(r), total: r.stops.length });
  }

  // Needs a look.
  const offRoute = new Map<string, number>();
  for (const r of running.values()) {
    if (r.working && !r.returning && !r.betweenRuns) {
      for (let i = r.current; i < r.stops.length; i++) {
        const s = r.stops[i];
        if (s.deliverBy == null || handled(s)) continue;
        const arrival = now + arrivalIn(r, i, now);
        if (arrival > s.deliverBy) {
          alerts.push({ id: `late:${r.id}:${s.id}`, kind: 'late', severity: 'needsAction', courierId: r.riderId, routeId: r.id, stopId: s.id, expected: arrival });
        }
      }
    }
    const heard = r.heardAt ?? r.startedAt ?? now;
    if (now - heard > QUIET_AFTER && !((r.pausedUntil ?? 0) > now)) {
      alerts.push({ id: `quiet:${r.id}:${heard}`, kind: 'quiet', severity: 'watch', courierId: r.riderId, routeId: r.id, lastHeard: heard });
    }
    // Left the route: off its line at two positions [LEFT_ROUTE_AFTER] apart or more. One line a leg (by the stop being
    // driven to), so each observer names it the same and clearing it holds.
    const off = offRouteOf(r, now, input.offRoute?.get(r.id));
    if (off) {
      offRoute.set(r.id, off.since);
      const next = r.stops[r.current];
      if (off.left) {
        alerts.push({ id: `leftRoute:${r.id}:${next.id}`, kind: 'leftRoute', severity: 'watch', courierId: r.riderId, routeId: r.id, stopId: next.id, metres: Math.round(off.away), handled: off.since, lastHeard: r.heardAt ?? undefined });
      }
    }
  }
  for (const [courier, list_] of handledBy) {
    list_.sort((a, b) => a.at - b.at);
    if (list_.length < 2) continue;
    const last = list_.slice(-2);
    if (last.some((h) => h.ok || excusedIds.has(h.stop.id))) continue;
    const route = running.get(courier)?.id ?? finished.get(courier)?.[0]?.id ?? '';
    alerts.push({
      id: `failed:${last[1].stop.id}`,
      kind: 'failedInARow',
      severity: 'needsAction',
      courierId: courier,
      routeId: route,
      stopId: last[1].stop.id,
      failures: last.map((h) => ({ stopId: h.stop.id, at: h.at })),
    });
  }
  for (const r of leftOpen) {
    alerts.push({
      id: `stillOpen:${r.id}`,
      kind: 'stillOpen',
      severity: 'watch',
      courierId: r.riderId,
      routeId: r.id,
      lastHeard: r.heardAt ?? r.startedAt ?? undefined,
      handled: r.startedAt ?? undefined,
      left: Math.max(0, r.stops.length - delivered(r) - notDelivered(r)),
    });
  }
  const shown = alerts.filter((a) => !cleared.has(a.id));
  const when = (a: Alert) => a.failures?.at(-1)?.at ?? a.handled ?? a.lastHeard ?? a.expected ?? now;
  shown.sort((a, b) => (a.severity !== b.severity ? (a.severity === 'needsAction' ? -1 : 1) : when(b) - when(a)));

  const openBy = new Map<string, Route>();
  for (const r of leftOpen) {
    const other = openBy.get(r.riderId);
    if (!other || (r.startedAt ?? 0) > (other.startedAt ?? 0)) openBy.set(r.riderId, r);
  }
  const presence = new Map((input.presence ?? []).map((p) => [p.uid, p]));
  const waitingBy = new Map<string, Route[]>();
  for (const r of waiting) waitingBy.set(r.riderId, [...(waitingBy.get(r.riderId) ?? []), r]);
  const money = noMoney();
  const name = (c: Courier) => (c.name ?? c.phone ?? c.uid).toLowerCase();
  const couriers: CourierDay[] = input.couriers
    .map((c) => {
      const run = running.get(c.uid) ?? null;
      const m = moneyOf(paidStops.get(c.uid) ?? [], onCall(c.uid));
      for (const k of Object.keys(money) as (keyof Money)[]) money[k] += m[k];
      return {
        courier: c,
        running: run,
        finished: [...(finished.get(c.uid) ?? [])].sort((a, b) => endOf(b) - endOf(a)),
        waiting: [...(waitingBy.get(c.uid) ?? [])].sort((a, b) => (a.sentAt ?? 0) - (b.sentAt ?? 0)),
        presence: presence.get(c.uid) ?? null,
        finishAt: run ? finishAt(run, now) : null,
        quiet: run != null && now - (run.heardAt ?? run.startedAt ?? now) > QUIET_AFTER,
        pausedForNight: run != null && (run.pausedUntil ?? 0) > now,
        leftOpen: openBy.get(c.uid) ?? null,
        money: m,
      };
    })
    .sort((a, b) => (a.running != null) === (b.running != null) ? name(a.courier).localeCompare(name(b.courier)) : a.running ? -1 : 1);

  events.sort((a, b) => b.at - a.at);
  return {
    day: today,
    zone,
    now,
    delivered: deliveredN,
    notDelivered: notDeliveredN,
    toGo,
    waitingStops: waiting.filter((r) => r.state === 'waiting').reduce((n, r) => n + r.stops.length, 0),
    couriers,
    routes: [...todays, ...waiting],
    events,
    alerts: shown,
    money,
    offRoute,
  };
}

// ── Stops not delivered (not_delivered.dart) ──────────────────────────────

/** How far back stops not delivered are offered (`notDeliveredWindow`: 28 days since 31 October, was a week). */
export const NOT_DELIVERED_WINDOW = 28 * DAY;

export interface NotDelivered {
  route: Route;
  stop: Stop;
  /** When it was marked not delivered, when known. */
  at: number | null;
  /** The route ended before it got here, so no one tried. */
  notReached: boolean;
}

/**
 * The phone's `notDelivered()` (not_delivered.dart): the stops not delivered on [routes] (any order) started within [NOT_DELIVERED_WINDOW] of [now],
 * and those a route ended early never reached: not put away ([dismissed], `routeId/stopId`), not delivered at the same
 * place on a later route, and one route's list for a place (its latest try). Several customers at one office on one
 * route are each their own stop, and a later delivery there covers only one of them. A stop tried again on its own
 * route (`retryOf`) counts as that try turned out. A handover between couriers isn't a customer's stop.
 */
export function notDeliveredStops(routes: Route[], now: number, dismissed: Set<string> = new Set()): NotDelivered[] {
  const same = (a: Stop, b: Stop) => (a.placeId != null && a.placeId === b.placeId) || a.address.trim().toLowerCase() === b.address.trim().toLowerCase();
  const newest = routes.filter((r) => r.startedAt != null).sort((a, b) => b.startedAt! - a.startedAt!);
  const out: NotDelivered[] = [];
  const seen: Stop[] = [];
  for (const r of newest) {
    if (now - r.startedAt! > NOT_DELIVERED_WINDOW) continue;
    // A route a server job ended: what became of its stops can't be said.
    const endedEarly = r.state === 'ended' && r.endedBy !== 'cleanup';
    const triedAgain = new Set(r.stops.map((s) => s.retryOf).filter((x): x is string => !!x));
    const here: Stop[] = [];
    for (const s of r.stops) {
      const untried = s.state === 'left';
      if (s.handover || triedAgain.has(s.id) || !(s.state === 'failed' || (endedEarly && untried))) continue;
      if (seen.some((o) => same(o, s))) continue;
      const before = here.filter((o) => same(o, s)).length;
      here.push(s);
      if (dismissed.has(`${r.id}/${s.id}`)) continue;
      const deliveredSince = newest.filter((o) => o.startedAt! > r.startedAt!).flatMap((o) => o.stops).filter((x) => x.state === 'done' && same(x, s)).length;
      if (deliveredSince > before) continue;
      out.push({ route: r, stop: s, at: s.state === 'failed' ? s.at : null, notReached: s.state !== 'failed' });
    }
    seen.push(...here);
  }
  return out;
}

/**
 * Needs a look's line for each courier still in the business: their stops not delivered in the last 28 days ([notDeliveredStops] over
 * their routes that aren't running), without those the business has decided about ([decided], `routeId/stopId`,
 * `stopDecisions`). Watch, then Needs action once one is over a day old. It has no Clear: deciding is what clears it.
 */
export function notDeliveredAlerts(routes: Route[], couriers: Courier[], now: number, decided: Set<string> = new Set()): Alert[] {
  const out: Alert[] = [];
  for (const c of couriers) {
    const theirs = routes.filter((r) => r.riderId === c.uid && (r.state === 'done' || r.state === 'ended'));
    const list = notDeliveredStops(theirs, now, decided);
    if (!list.length) continue;
    const when = (x: NotDelivered) => x.at ?? x.route.endedAt ?? x.route.completedAt ?? x.route.startedAt ?? now;
    const oldest = Math.min(...list.map(when));
    out.push({
      id: `notDelivered:${c.uid}`,
      kind: 'notDelivered',
      severity: now - oldest > DAY ? 'needsAction' : 'watch',
      courierId: c.uid,
      routeId: list[0].route.id,
      handled: Math.max(...list.map(when)),
      left: list.length,
      missed: list.map((x) => ({ routeId: x.route.id, stopId: x.stop.id, at: x.at, notReached: x.notReached })),
    });
  }
  return out;
}

/** Sent and not started after this long: a Watch (sent_routes.dart `unstartedAfter`). */
export const UNSTARTED_AFTER = 30 * MINUTE;

/** A sent route sitting unstarted too long; for a courier on a route, the half hour runs from when they're due to finish. */
export function leftUnstarted(r: Route, now: number, busyUntil?: number | null): boolean {
  if (r.state !== 'waiting' || r.sentAt == null) return false;
  const from = busyUntil != null && busyUntil > r.sentAt ? busyUntil : r.sentAt;
  return now - from > UNSTARTED_AFTER;
}

// ── Settling up (settling.dart) ────────────────────────────────────────────

export interface RouteMoney {
  routeId: string;
  riderId: string;
  handIn: number;
  cash: number;
  toCourier: number;
  feesWithBusiness: number;
  currency: string;
  day: string | null;
  at: number | null;
}

export function readRouteMoney(id: string, d: Doc): RouteMoney | null {
  if (typeof d.riderId !== 'string' || typeof d.handIn !== 'number') return null;
  return {
    routeId: id,
    riderId: d.riderId,
    handIn: Math.trunc(d.handIn),
    cash: int(d.cash) ?? 0,
    toCourier: int(d.toCourier) ?? 0,
    feesWithBusiness: int(d.feesWithBusiness) ?? 0,
    currency: str(d.currency) ?? 'NGN',
    day: str(d.day) ?? null,
    at: ms(d.at),
  };
}

export interface HandIn {
  id: string;
  riderId: string;
  amount: number;
  received: number | null;
  out: boolean;
  status: 'waiting' | 'confirmed' | 'notReceived';
  how: string;
  at: number | null;
  confirmedAt: number | null;
  zeroThrough: number | null;
}

export function readHandIn(id: string, d: Doc): HandIn | null {
  if (typeof d.riderId !== 'string' || typeof d.amount !== 'number') return null;
  const status = d.status === 'confirmed' || d.status === 'notReceived' ? d.status : 'waiting';
  return {
    id,
    riderId: d.riderId,
    amount: Math.trunc(d.amount),
    received: int(d.received) ?? null,
    out: d.out === true,
    status,
    how: str(d.how) ?? 'cash',
    at: ms(d.at),
    confirmedAt: ms(d.confirmedAt),
    zeroThrough: ms(d.zeroThrough),
  };
}

const settles = (h: HandIn) => (h.status !== 'confirmed' ? 0 : h.out ? -(h.received ?? h.amount) : (h.received ?? h.amount));

/** A courier's balance from their last zero point: above zero, the business's money they hold (`CourierBalance.of`). */
export function balanceOf(riderId: string, routes: RouteMoney[], handIns: HandIn[]): { amount: number; waiting: HandIn[]; routes: RouteMoney[] } {
  const mine = handIns.filter((h) => h.riderId === riderId);
  const confirmedAt = (h: HandIn) => h.confirmedAt ?? h.at ?? Date.UTC(2100, 0, 1);
  let zero: HandIn | null = null;
  for (const h of mine) if (h.status === 'confirmed' && h.zeroThrough != null && (!zero || confirmedAt(h) > confirmedAt(zero))) zero = h;
  const afterZero = (h: HandIn) => !zero || (h.id !== zero.id && confirmedAt(h) > confirmedAt(zero));
  const money = routes.filter((r) => r.riderId === riderId && (!zero || r.at == null || r.at > (zero.zeroThrough ?? 0)));
  const done = mine.filter((h) => h.status === 'confirmed' && afterZero(h));
  return {
    amount: money.reduce((s, r) => s + r.handIn, 0) - done.reduce((s, h) => s + settles(h), 0),
    waiting: mine.filter((h) => h.status === 'waiting'),
    routes: money,
  };
}

// ── Stats over past days (CourierStats) ────────────────────────────────────

export interface Tally {
  delivered: number;
  notDelivered: number;
  /** Delivered on the first visit. */
  firstTime: number;
  /** The stops the first-time rate is out of: handled, less a closed shop or another day. */
  rated: number;
  /** Delivered by the deliver-by time, of the stops that had one. */
  onTimeMet: number;
  onTimeOf: number;
  /** The late ones in the morning, afternoon and evening of the business's day (`_partOfDay`): the phone's "Both late ones were Tuesday afternoon." */
  lateParts: number[];
  /** Stops whose customer had their link, and how many of those were delivered: the phone's tip. */
  linked: number;
  linkedDelivered: number;
  reasons: Partial<Record<Reason, number>>;
  /** Handled in each hour of the business's day (0–23). */
  byHour: number[];
  km: number;
  routes: number;
  cash: number;
  transfer: number;
  pos: number;
  collected: number;
}

export const noTally = (): Tally => ({
  delivered: 0,
  notDelivered: 0,
  firstTime: 0,
  rated: 0,
  onTimeMet: 0,
  onTimeOf: 0,
  lateParts: [0, 0, 0],
  linked: 0,
  linkedDelivered: 0,
  reasons: {},
  byHour: Array.from({ length: 24 }, () => 0),
  km: 0,
  routes: 0,
  cash: 0,
  transfer: 0,
  pos: 0,
  collected: 0,
});

/** A tally as the server stores it (daily_totals.ts), anything missing as nothing. */
export function readTally(v: unknown): Tally {
  const d = record(v);
  const t = noTally();
  for (const k of ['delivered', 'notDelivered', 'firstTime', 'rated', 'onTimeMet', 'onTimeOf', 'linked', 'linkedDelivered', 'km', 'routes', 'cash', 'transfer', 'pos', 'collected'] as const) {
    t[k] = num(d[k]) ?? 0;
  }
  const parts = list(d.lateParts);
  for (let i = 0; i < 3; i++) t.lateParts[i] = num(parts[i]) ?? 0;
  for (const [k, n] of Object.entries(record(d.reasons))) if (k in REASONS && typeof n === 'number') t.reasons[k as Reason] = n;
  const hours = list(d.byHour);
  for (let h = 0; h < 24; h++) t.byHour[h] = num(hours[h]) ?? 0;
  return t;
}

export function addTally(into: Tally, t: Tally): Tally {
  for (const k of ['delivered', 'notDelivered', 'firstTime', 'rated', 'onTimeMet', 'onTimeOf', 'linked', 'linkedDelivered', 'km', 'routes', 'cash', 'transfer', 'pos', 'collected'] as const) {
    into[k] += t[k];
  }
  t.lateParts.forEach((v, i) => (into.lateParts[i] += v));
  for (const [k, v] of Object.entries(t.reasons) as [Reason, number][]) into.reasons[k] = (into.reasons[k] ?? 0) + v;
  t.byHour.forEach((v, i) => (into.byHour[i] += v));
  return into;
}

/** One stop's share of the numbers. */
function tallyStop(t: Tally, s: Stop, zone: string): void {
  if (!handled(s)) return;
  if (s.state === 'done') {
    t.delivered++;
    if (s.attempts === 0) t.firstTime++;
  } else {
    t.notDelivered++;
    if (s.reason) t.reasons[s.reason] = (t.reasons[s.reason] ?? 0) + 1;
  }
  if (!(s.state === 'failed' && excused(s.reason))) t.rated++;
  if (s.deliverBy != null && s.at != null) {
    t.onTimeOf++;
    if (s.at <= s.deliverBy) t.onTimeMet++;
    else {
      const hour = wall(s.at, zone).hour;
      t.lateParts[hour < 12 ? 0 : hour < 17 ? 1 : 2]++;
    }
  }
  if (s.linkSent) {
    t.linked++;
    if (s.state === 'done') t.linkedDelivered++;
  }
  if (s.at != null) t.byHour[wall(s.at, zone).hour]++;
  if (paid(s) && toCollect(s) > 0) {
    const amount = s.paidAmount ?? toCollect(s);
    t.collected += amount;
    if (s.paidHow === 'cash') t.cash += amount;
    else if (s.paidHow === 'transfer') t.transfer += amount;
    else if (s.paidHow === 'pos') t.pos += amount;
  }
}

/**
 * Each day's numbers from the routes that ended (and with [opts.running], those still running), by the business's day
 * a stop was handled (`CourierStats.handledStops`: a stop's day is when it was handled, else when its route ended), all
 * couriers and each one. Routes and km go on the day a route started, and a route ended early counts the share of it
 * that was run (`CourierStats._km`, `tallyDays` in lib/services/business/day_record.dart).
 */
export function tallyDays(routes: Route[], zone: string, opts: { running?: boolean } = {}): Map<string, { all: Tally; byCourier: Map<string, Tally> }> {
  const out = new Map<string, { all: Tally; byCourier: Map<string, Tally> }>();
  const of = (day: string, courier: string) => {
    let d = out.get(day);
    if (!d) out.set(day, (d = { all: noTally(), byCourier: new Map() }));
    let c = d.byCourier.get(courier);
    if (!c) d.byCourier.set(courier, (c = noTally()));
    return [d.all, c] as const;
  };
  for (const r of routes) {
    // Today so far counts the routes still running too ([opts.running]); a past day only the ones that ended.
    if (r.state !== 'done' && r.state !== 'ended' && !(opts.running && r.state === 'running')) continue;
    const ended = r.completedAt ?? r.endedAt ?? r.startedAt;
    if (r.startedAt != null) {
      const [all, mine] = of(dayOf(r.startedAt, zone), r.riderId);
      const km = r.state === 'ended' && r.stops.length ? (r.km * r.stops.filter(handled).length) / r.stops.length : r.km;
      all.routes++;
      mine.routes++;
      all.km += km;
      mine.km += km;
    }
    for (const s of r.stops) {
      if (!handled(s)) continue;
      const t = s.at ?? ended;
      if (t == null) continue;
      for (const into of of(dayOf(t, zone), r.riderId)) tallyStop(into, s, zone);
    }
  }
  return out;
}

/** "94%" of a rate, or null with nothing to rate. */
export const rate = (n: number, of: number) => (of > 0 ? n / of : null);
